import { useEffect, useRef, useState } from 'react';

export type ConnectionQuality = 'good' | 'poor' | 'bad' | null;

export interface VideoStats {
  width: number | null;
  height: number | null;
  framesPerSecond: number | null;
  bitrateKbps: number | null;
  framesDroppedPercent: number | null;
}

export interface QualityStats {
  quality: ConnectionQuality;
  packetLossPercent: number | null;
  roundTripTimeMs: number | null;
  video: VideoStats;
}

interface PrevInboundRtp {
  packets: number;
  lost: number;
  bytes: number;
  timestamp: number;
  framesReceived: number;
  framesDropped: number;
}

const POLL_INTERVAL_MS = 2000;

const emptyVideoStats: VideoStats = {
  width: null,
  height: null,
  framesPerSecond: null,
  bitrateKbps: null,
  framesDroppedPercent: null,
};

/**
 * Polls RTCPeerConnection.getStats() to derive connection quality
 * including video-specific metrics (resolution, fps, bitrate, dropped frames).
 *
 * Quality thresholds:
 * - good: <2% packet loss, <300ms RTT, >=15fps, <3% frames dropped
 * - poor: <5% packet loss, <500ms RTT, >=8fps, <10% frames dropped
 * - bad: anything worse
 */
export function useConnectionQuality(
  peerConnection: RTCPeerConnection | null,
  isActive: boolean,
): QualityStats {
  const [stats, setStats] = useState<QualityStats>({
    quality: null,
    packetLossPercent: null,
    roundTripTimeMs: null,
    video: emptyVideoStats,
  });

  const prevRef = useRef<Map<string, PrevInboundRtp>>(new Map());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isActive || !peerConnection) {
      setStats({
        quality: null,
        packetLossPercent: null,
        roundTripTimeMs: null,
        video: emptyVideoStats,
      });
      prevRef.current.clear();
      return;
    }

    const poll = async () => {
      if (!peerConnection || peerConnection.connectionState !== 'connected') {
        return;
      }

      try {
        const report = await peerConnection.getStats();

        let totalPacketsReceived = 0;
        let totalPacketsLost = 0;
        let rtt: number | null = null;

        let videoWidth: number | null = null;
        let videoHeight: number | null = null;
        let videoFps: number | null = null;
        let videoBitrateKbps: number | null = null;
        let videoFramesDroppedPercent: number | null = null;

        report.forEach((stat) => {
          if (stat.type === 'inbound-rtp' && !stat.isRemote) {
            const prev = prevRef.current.get(stat.id);
            const currentPackets = stat.packetsReceived ?? 0;
            const currentLost = stat.packetsLost ?? 0;
            const currentBytes = stat.bytesReceived ?? 0;
            const currentTimestamp = stat.timestamp ?? 0;
            const currentFramesReceived = stat.framesReceived ?? 0;
            const currentFramesDropped = stat.framesDropped ?? 0;

            if (prev) {
              const deltaReceived = currentPackets - prev.packets;
              const deltaLost = currentLost - prev.lost;
              totalPacketsReceived += deltaReceived;
              totalPacketsLost += deltaLost;

              // Video-specific: bitrate and frames dropped (only for video tracks)
              if (stat.kind === 'video') {
                const deltaBytes = currentBytes - prev.bytes;
                const deltaTimeS = (currentTimestamp - prev.timestamp) / 1000;

                if (deltaTimeS > 0) {
                  videoBitrateKbps = Math.round(
                    (deltaBytes * 8) / deltaTimeS / 1000,
                  );
                }

                const deltaFramesReceived =
                  currentFramesReceived - prev.framesReceived;
                const deltaFramesDropped =
                  currentFramesDropped - prev.framesDropped;
                const totalDeltaFrames =
                  deltaFramesReceived + deltaFramesDropped;

                if (totalDeltaFrames > 0) {
                  videoFramesDroppedPercent =
                    Math.round((deltaFramesDropped / totalDeltaFrames) * 1000) /
                    10;
                }
              }
            }

            // Video resolution and fps (available directly on the stat)
            if (stat.kind === 'video') {
              if (stat.frameWidth) videoWidth = stat.frameWidth;
              if (stat.frameHeight) videoHeight = stat.frameHeight;
              if (stat.framesPerSecond != null)
                videoFps = Math.round(stat.framesPerSecond);
            }

            prevRef.current.set(stat.id, {
              packets: currentPackets,
              lost: currentLost,
              bytes: currentBytes,
              timestamp: currentTimestamp,
              framesReceived: currentFramesReceived,
              framesDropped: currentFramesDropped,
            });
          }

          // Active candidate pair for RTT
          if (
            stat.type === 'candidate-pair' &&
            stat.state === 'succeeded' &&
            stat.currentRoundTripTime != null
          ) {
            rtt = stat.currentRoundTripTime * 1000;
          }
        });

        const totalPackets = totalPacketsReceived + totalPacketsLost;
        const lossPercent =
          totalPackets > 0 ? (totalPacketsLost / totalPackets) * 100 : null;

        let quality: ConnectionQuality;
        if (lossPercent === null && rtt === null && videoFps === null) {
          quality = null;
        } else {
          const loss = lossPercent ?? 0;
          const latency = rtt ?? 0;
          const fps = videoFps ?? 30;
          const dropped = videoFramesDroppedPercent ?? 0;

          if (loss < 2 && latency < 300 && fps >= 15 && dropped < 3) {
            quality = 'good';
          } else if (loss < 5 && latency < 500 && fps >= 8 && dropped < 10) {
            quality = 'poor';
          } else {
            quality = 'bad';
          }
        }

        setStats({
          quality,
          packetLossPercent:
            lossPercent !== null ? Math.round(lossPercent * 10) / 10 : null,
          roundTripTimeMs: rtt !== null ? Math.round(rtt) : null,
          video: {
            width: videoWidth,
            height: videoHeight,
            framesPerSecond: videoFps,
            bitrateKbps: videoBitrateKbps,
            framesDroppedPercent: videoFramesDroppedPercent,
          },
        });
      } catch {
        // getStats can throw if connection is closing
      }
    };

    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    const prev = prevRef.current;

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      prev.clear();
    };
  }, [isActive, peerConnection]);

  return stats;
}
